import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import { useAppSelector, useAppDispatch, loadSuggestedTracks } from '../../redux';
import { Project, Client, Track } from '../../models';
import { loadProjects, loadActiveTrack, stopActiveTrack, createTrack, updateTrack, reportTrackStartError } from '../../redux';
import { Button, SelectField, Icon, ResumeTrack, ClientSelectField, Tooltip, ToggleField, TrackEditModal } from '..';
import {
    matchingProjectsForClient,
    projectSelectOptions,
} from '../../utils/projectSelectOptions';
import { applyActiveTrackDocumentTitle } from '../../utils/changeDocumentTitle';
import {
    handleChangeMiddleClick,
    openChangeInNewBrowserTab,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';
import moment from 'moment';

interface ActiveTrackProps {}

function ActiveTrack(props: ActiveTrackProps) {
    const { t, i18n } = useTranslation();
    const navigate    = useNavigate();
    const dispatch    = useAppDispatch();

    const [activeDuration, setActiveDuration]    = useState("00:00");
    const [detailsAreVisible, setDetailsVisible] = useState(false);
    const [mileageOpen, setMileageOpen]          = useState(false);
    const [clientError, setClientError]          = useState<string | undefined>(undefined);
    const [editingId, setEditingId]              = useState<string | undefined>(undefined);
    const titleInputRef                          = useRef<HTMLInputElement>(null);

    const { projects } = useAppSelector(state => state.projects);
    const { user }     = useAppSelector(state => state.auth);
    const { setting }  = useAppSelector(state => state.settings);
    const mileageEnabled = !!setting?.mileage?.enabled;

    const {
        suggested, loadingSuggested,
        tracks,
        active, loadingActive,
        stoppingActive, creatingError
    } = useAppSelector(state => state.tracks);

    const editingTrack = editingId
        ? (tracks?.find((track: Track) => track._id === editingId)
            || suggested?.find((track: Track) => track._id === editingId))
        : undefined;

    useEffect(() => {
        let interval: any;
        if (active)
            interval = setInterval(() => {
                setActiveDuration(active?.formattedDuration());
            }, 1000);

        return () => interval && clearInterval(interval);
    }, [active]);

    useEffect(() => {
        dispatch(loadProjects());
        dispatch(loadActiveTrack());
    }, [dispatch]);

    useEffect(() => {
        if (detailsAreVisible && !initialValues?.title)
            dispatch(loadSuggestedTracks({ limit: 10 }));
    }, [detailsAreVisible]);

    useEffect(() => {
        if (!detailsAreVisible || active || loadingActive === 'pending')
            return;
        const timer = window.setTimeout(() => {
            titleInputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [detailsAreVisible, active, loadingActive]);

    const resolveProject = (values: AnyObj): Project | undefined => {
        const projectId = values?.project?._id || values?.project;
        if (!projectId || typeof projectId !== 'string')
            return undefined;

        return projects.find((project: Project) => project._id === projectId);
    };

    const assignDefaultClient = (properties: AnyObj, project?: Project) => {
        const hasClient = !!(properties.client?._id || (typeof properties.client === 'string' && properties.client));
        if (hasClient)
            return;

        if (project?.clients?.length === 1 && typeof project.clients[0] !== 'string')
            properties.client = project.clients[0];
    };

    const stop = () => {
        dispatch(stopActiveTrack((err: Error | null) => setDetailsVisible(false)));
    };

    const submit = function (values: AnyObj) : void {
        const { startAtTime, ...properties } = values;
        const [hours, minutes] = startAtTime.split(':');

        if (!properties.startAt)
            properties.startAt = moment();

        properties.startAt = properties.startAt
            .hours(parseInt(hours))
            .minutes(parseInt(minutes))
            .seconds(0)
            .milliseconds(0);

        // Clamp small clock/skew drift so "now" from the time field is not rejected.
        const now = moment();
        if (properties.startAt.isAfter(now) && properties.startAt.diff(now, 'seconds') <= 120)
            properties.startAt = now;

        const project = resolveProject(properties);
        assignDefaultClient(properties, project);

        const clientId = properties.client?._id || (typeof properties.client === 'string' ? properties.client : undefined);
        if (!clientId) {
            setClientError(t('tracks.select_client') || undefined);
            return;
        }

        setClientError(undefined);

        if (!properties.project)
            return;

        const hasChange = !!(properties.change?._id || (typeof properties.change === 'string' && properties.change));
        properties.billingMode = properties.billingMode || 'hourly';
        if (properties.billingMode === 'floor' && !hasChange)
            properties.billingMode = 'hourly';
        properties.billingDev = properties.billingMode === 'free'
            ? false
            : !!properties.billingDev;
        delete properties.billable;

        if (mileageEnabled) {
            if (properties.km === '' || properties.km == null)
                delete properties.km;
            else {
                const km = Number(properties.km);
                if (!Number.isFinite(km) || km < 0)
                    delete properties.km;
                else
                    properties.km = km;
            }
        } else {
            delete properties.km;
        }

        dispatch(properties._id ?
            updateTrack(properties) :
            createTrack(properties, {}, (err: Error | null) => {
                if (!err)
                    dispatch(loadActiveTrack());
            })
        );
    }

    const formatTrackStartError = (message: string | null | undefined) => {
        if (!message)
            return undefined;
        if (/client is required/i.test(message))
            return t('tracks.start_failed_client');
        if (/can not be in the future/i.test(message))
            return t('tracks.start_failed_future');
        return t('tracks.start_failed');
    };

    const trackStartError = formatTrackStartError(creatingError);

    const toggleDetails = () => {
        const next = !detailsAreVisible;
        setDetailsVisible(next);
        if (!next)
            setMileageOpen(false);
        setClientError(undefined);
    };

    const handleKeyPress = (e: AnyObj) => {
        if (e.key === 'Enter' || e.code === 'Enter') {
            // nada for now
        } else if (e.key === 'Escape' || e.code === 'Escape') {
            setDetailsVisible(false);
        }
    }

    const activeProject = active?.project
        ? projects.find((project: Project) => project._id === active.project._id)
        : undefined;
    const linkedChange = (active?.change && typeof active.change.slugName === 'function')
        ? active.change
        : undefined;

    const linkedProject = activeProject || active?.project || linkedChange?.project;

    const openLinkedChange = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (!linkedChange?._id || !linkedProject)
            return;

        if (e.button === 1) {
            openChangeInNewBrowserTab(linkedChange, linkedProject);
            return;
        }

        setDetailsVisible(false);
        navigate(Project.path(linkedProject, 'change', linkedChange._id));
    };

    const openLinkedChangeMiddle = (e: React.MouseEvent) => {
        handleChangeMiddleClick(e, linkedChange, linkedProject);
    };

    const defaultProjectId = !active
        ? (user?.trackingPreferences?.defaultProjectId || undefined)
        : undefined;
    const preferredProject = defaultProjectId
        ? projects.find((project: Project) => project._id === defaultProjectId)
        : undefined;

    const defaultClient = active?.client
        || (
            activeProject?.clients?.length === 1
            && typeof activeProject.clients[0] !== 'string'
                ? activeProject.clients[0] as Client
                : undefined
        )
        || (
            !active
            && preferredProject?.clients?.length === 1
            && typeof preferredProject.clients[0] !== 'string'
                ? preferredProject.clients[0] as Client
                : undefined
        );

    const hasLinkedChange = !!(linkedChange?._id || active?.change?._id);
    const preferredBillingMode = user?.trackingPreferences?.defaultBillingMode;
    const initialBillingMode = (!hasLinkedChange && active?.billingMode === 'floor')
        ? 'hourly'
        : (active?.billingMode
            || preferredBillingMode
            || 'hourly');

    const initialValues = {
        ...active,
        client: defaultClient,
        ...(defaultProjectId ? { project: { _id: defaultProjectId } } : {}),
        description: active?.description || '',
        billingMode: initialBillingMode,
        billingDev: !!active?.billingDev,
        startAtTime: (active?.startAt || moment()).format('HH:mm')
    };

    const billingModeOptions = [
        { value: 'hourly', label: t('tracks.billing_mode_hourly') },
        { value: 'package', label: t('tracks.billing_mode_package') },
        { value: 'free', label: t('tracks.billing_mode_free') },
        ...(hasLinkedChange
            ? [{ value: 'floor', label: t('tracks.billing_mode_floor') }]
            : []),
    ];

    if (active)
        applyActiveTrackDocumentTitle(activeDuration, active.title);

    return (
        <div className="relative h-8">
            {detailsAreVisible && (
                <div onClick={toggleDetails} className="fixed w-[100vw] h-[100vh] top-0 left-0 z-1 bg-gray-900/50 z-10"></div>
            )}
            <div className="flex flex-col absolute z-10 right-0">
                <div className={`flex flex-row space-x-1 px-2 py-1 ring-1 bg-gray-900 rounded-md ${detailsAreVisible ? 'rounded-b-none' : ''} justify-between items-center`}>
                    <div className="flex flex-row space-x-1 justify-start items-center min-w-[1rem]">
                        <Icon type={(loadingActive === 'pending') ? 'loading' : 'timer'} color="white" className={`inline ${active ? 'animate-pulse' : ''}`} size={5} />
                        {active && (
                            <>
                                <span className="block font-netox font-bold text-sm text-white text-left w-15">
                                    {activeDuration}
                                </span>
                                {linkedChange ? (
                                    <Tooltip content={t('tracks.open_change', { slug: linkedChange.slugName() })}>
                                        <span
                                            className="font-netox font-bold text-xs text-white text-left w-12 lg:w-48 truncate cursor-pointer hover:underline"
                                            onClick={openLinkedChange}
                                            onMouseDown={preventMiddleClickAutoscroll}
                                            onAuxClick={openLinkedChangeMiddle}
                                            role="link"
                                        >
                                            |&nbsp;{active.title}
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <span className="font-netox font-bold text-xs text-white text-left w-12 lg:w-48 truncate">
                                        |&nbsp;{active.title}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    <div className="flex items-center cursor-pointer">
                        {active && (
                            <div className="flex items-center" onClick={() => stop()}>
                                <Icon type={(stoppingActive === 'pending') ? 'loading' : 'stop'} color="red-500" className="inline ml-2" size={5} />
                            </div>
                        )}
                        <div className="flex items-center" onClick={() => toggleDetails()}>
                            <Icon type={(loadingActive === 'pending') ? 'loading' : (detailsAreVisible ? 'up' : 'down')} color="white" className="inline ml-1" size={5} />
                        </div>
                    </div>
                </div>
                {trackStartError && !detailsAreVisible && (
                    <div
                        className="mt-1 max-w-xs rounded-md bg-red-50 px-2 py-1 text-right text-xs font-bold text-red-600 shadow cursor-pointer"
                        onClick={() => dispatch(reportTrackStartError(null))}
                        role="alert"
                    >
                        {trackStartError}
                    </div>
                )}
            </div>
            {detailsAreVisible && (
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={(values, { setSubmitting }) => {
                        submit(values);
                    }}
                >
                    {({ values, dirty, setFieldValue }) => {
                        const formProject = resolveProject(values) || (active?.project
                            ? projects.find((p: Project) => p._id === active.project._id)
                            : undefined);
                        const selectedProject   = formProject || active?.project;
                        const lockedClient      = active?.client;
                        const selectedClientId  = values.client?._id
                            || (typeof values.client === 'string' ? values.client : undefined)
                            || lockedClient?._id;
                        const hasClient         = !!selectedClientId || !!lockedClient?._id;
                        const showClientSelect  = !lockedClient && !!values.title;
                        const projectsOptions   = projectSelectOptions(
                            projects,
                            selectedClientId,
                            defaultProjectId
                        );
                        const showProjectSelect = !active?.project
                            && (!!values.title)
                            && (hasClient || !!defaultProjectId);

                        return (
                        <Form>
                            <div className="rounded-md ring-1 md:rounded-tr-none fixed md:absolute top-[50%] md:top-7 left-[50%] md:left-auto translate-x-[-50%] md:translate-x-0 translate-y-[-50%] md:translate-y-0 md:right-0 z-10 bg-gray-900 w-[20rem] md:w-[30rem] p-0">
                                <div className="flex flex-col gap-y-6 justify-center p-2 rounded-md text-gray-900 dark:text-gray-100">
                                    {trackStartError && (
                                        <div
                                            className="text-center text-xs font-bold text-red-400 cursor-pointer"
                                            onClick={() => dispatch(reportTrackStartError(null))}
                                            role="alert"
                                        >
                                            {trackStartError}
                                        </div>
                                    )}
                                    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 items-start md:items-center">
                                        <div className="flex flex-row w-full md:w-fit justify-start items-center">
                                            <Icon type="from" color="white" className="inline mr-1" />
                                            <div className="flex-1 flex justify-center w-full md:w-fit focus:ring-0 border-0 shadow-sm font-bold text-sm sm:leading-6">
                                                <Field
                                                    type="time"
                                                    name="startAtTime"
                                                    className="block text-white bg-gray-900 border-0 focus:ring-0 border-white border-b-2"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-row w-full justify-start items-center">
                                            <Icon type="comment" color="white" className="inline ml-0 md:ml-3 mr-1" />
                                            <div className="flex-1 flex justify-center w-full md:w-fit focus:ring-0 shadow-sm font-bold text-sm sm:leading-6">
                                                <Field
                                                    type="text"
                                                    name="title"
                                                    innerRef={titleInputRef}
                                                    className="block text-center text-white bg-gray-900 border-0 placeholder:text-sm focus:ring-0 border-white border-b-2"
                                                    placeholder={t('tracks.title_placeholder') || ''}
                                                    onKeyDown={handleKeyPress}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                    {!values.title &&
                                        <div className="flex flex-col space-y-0">
                                            {(loadingSuggested === 'pending') ? (
                                                <div className="flex justify-center items-center">
                                                    <Icon type="loading" color="white" className={`inline ${active ? 'animate-pulse' : ''}`} size={5} />
                                                </div>
                                            ) : (loadingSuggested !== 'pending') && suggested.map((track: Track) => (
                                                <div
                                                    key={track._id}
                                                    className="flex flex-col md:flex-row first:rounded-t last:rounded-b border border-gray-800 border-b-0 last:border-b-0 hover:bg-gray-800 p-1 pt-0 cursor-pointer"
                                                    onClick={() => setEditingId(track._id)}
                                                >
                                                    <div className="flex-1 flex-col space-y-0">
                                                        <span className="block text-white text-center md:text-left text-sm font-bold w-full truncate md:w-[24rem] lg:w-100 truncate">
                                                            {track.title}
                                                        </span>
                                                        <div className="flex flex-col items-center md:items-start lg:flex-row space-x-0 lg:space-x-2">
                                                            <div
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(Project.path(track.project));
                                                                }}
                                                                className="cursor-pointer"
                                                            >
                                                                <div className="flex flex-row space-x-1.5 items-center text-white">
                                                                    <Icon type="project" className="" size={3} color="white" />
                                                                    <div className="text-xs font-light w-full md:w-64 truncate">{track.project?.name}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="flex-auto flex justify-center md:justify-end items-center"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <ResumeTrack
                                                            track={track}
                                                            className="w-12"
                                                            hideRemove
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                    </div>
                                    {values.title && (
                                    <div className="flex flex-col gap-y-0">
                                        {lockedClient ? (
                                        <div className="flex justify-between items-center text-white text-xs font-bold py-2">
                                            <div className="flex items-center text-white text-xs font-bold">
                                                <Icon type="user" color="white" className="inline mr-1.5" />
                                                {typeof lockedClient.name === 'function'
                                                    ? lockedClient.name()
                                                    : (lockedClient as AnyObj).company || t('tracks.client')}
                                            </div>
                                        </div>
                                        ) : (showClientSelect && (
                                            <div className="flex flex-col gap-y-1 py-2">
                                                <div className="flex flex-row items-center text-white text-xs font-bold mb-1">
                                                    <Icon type="user" color="white" className="inline mr-1.5" />
                                                    {t('tracks.client')}
                                                </div>
                                                <ClientSelectField
                                                    name="client"
                                                    placeholder={t('tracks.select_client') || ''}
                                                    searchInputClassName="!bg-gray-800 !text-white !ring-white placeholder:!text-gray-400"
                                                    resultsClassName="border-gray-700 bg-gray-800 text-white"
                                                    resultItemClassName="border-b border-gray-700 p-2 hover:bg-gray-700 cursor-pointer text-sm text-white"
                                                    selectedClassName="!bg-gray-800 !text-white"
                                                    autoFocus={!!clientError}
                                                    onChange={(client) => {
                                                        setClientError(undefined);
                                                        const currentProjectId = values.project?._id
                                                            || (typeof values.project === 'string' ? values.project : undefined);
                                                        // Never clear/replace an already selected project (esp. user default).
                                                        if (currentProjectId)
                                                            return;
                                                        if (defaultProjectId) {
                                                            setFieldValue('project._id', defaultProjectId, false);
                                                            return;
                                                        }
                                                        const matching = matchingProjectsForClient(projects, client?._id);
                                                        if (matching.length === 1)
                                                            setFieldValue('project._id', matching[0]._id, false);
                                                    }}
                                                />
                                            </div>
                                        ))}
                                        {active?.project ? (
                                            <div className="flex justify-between items-center text-white text-xs font-bold py-2">
                                                <div className="flex items-center text-white text-xs font-bold">
                                                    <Icon type="project" color="white" className="inline mr-1.5" />
                                                    {active.project.name}
                                                </div>
                                            </div>
                                        ) : (showProjectSelect && (
                                            <div className="flex flex-row items-center">
                                                <Icon type="project" color="white" className="inline mr-1" />
                                                <div className="flex-1 w-fit">
                                                    <SelectField
                                                        name="project._id"
                                                        placeholder={t('tracks.select_project') || ''}
                                                        containerClassName="block w-full focus:ring-0 border-0 border-b-2 border-white bg-transparent py-1.5 shadow-sm font-bold text-sm text-white sm:leading-6 -mb-1"
                                                        optionsContainerClassName="rounded-t-none !bg-gray-800 !text-white border border-gray-700"
                                                        optionsClassName="!text-white data-[headlessui-state=active]:!bg-gray-700"
                                                        options={projectsOptions}
                                                        optionsHeight={'max-h-96'}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        {linkedChange && (
                                        <div
                                            className="flex justify-between items-center text-white text-xs font-bold py-2 cursor-pointer hover:underline"
                                            onClick={openLinkedChange}
                                            onMouseDown={preventMiddleClickAutoscroll}
                                            onAuxClick={openLinkedChangeMiddle}
                                        >
                                            <div className="flex items-center text-white text-xs font-bold min-w-0">
                                                <Icon type={linkedChange.iconName()} color="white" className="inline mr-1.5" />
                                                <span className="truncate">
                                                    {linkedChange.slugName()}&nbsp;
                                                    {linkedChange.localizedTitle(i18n.language)}
                                                </span>
                                            </div>
                                            <Icon type="view" color="white" className="inline ml-1.5" size={4} />
                                        </div>
                                        )}
                                        <div className="flex flex-col gap-y-1 py-2">
                                            <div className="flex flex-row items-center text-white text-xs font-bold">
                                                <Icon type="comment" color="white" className="inline mr-1.5" />
                                                {t('tracks.description')}
                                            </div>
                                            <Field
                                                as="textarea"
                                                name="description"
                                                rows={3}
                                                placeholder={t('tracks.description_placeholder') || ''}
                                                className="block w-full rounded-md border-0 py-1.5 px-2 text-white bg-gray-800 placeholder:text-gray-400 focus:ring-1 focus:ring-white text-xs"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-y-2 py-2">
                                            <div className="flex flex-row items-center text-white text-xs font-bold">
                                                <Icon type="quote" color="white" className="inline mr-1.5" />
                                                {t('tracks.billing')}
                                            </div>
                                            <SelectField
                                                name="billingMode"
                                                placeholder={t('tracks.billing') || ''}
                                                containerClassName="block w-full focus:ring-0 border-0 border-b-2 border-white bg-transparent py-1.5 shadow-sm font-bold text-sm text-white sm:leading-6"
                                                optionsContainerClassName="rounded-t-none !bg-gray-800 !text-white border border-gray-700"
                                                optionsClassName="!text-white !bg-transparent hover:!bg-gray-700"
                                                options={billingModeOptions}
                                                optionsHeight={'max-h-96'}
                                                onChange={(mode: string) => {
                                                    if (mode === 'free')
                                                        setFieldValue('billingDev', false);
                                                }}
                                            />
                                            {values.billingMode !== 'free' && (
                                            <div className="flex justify-between items-center text-white text-xs font-bold pt-1">
                                                <span>{t('tracks.billing_dev')}</span>
                                                <ToggleField
                                                    name="billingDev"
                                                    className="[&>label]:!text-white"
                                                />
                                            </div>
                                            )}
                                        </div>
                                        {mileageEnabled && values.title && (
                                            <div className="mt-3 rounded-md border border-gray-600 bg-gray-800/80">
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-gray-700 focus:outline-none"
                                                    onClick={() => setMileageOpen(!mileageOpen)}
                                                    aria-expanded={mileageOpen}
                                                >
                                                    <Icon
                                                        type={mileageOpen ? 'up' : 'down'}
                                                        color="white"
                                                        size={4}
                                                        className="shrink-0"
                                                    />
                                                    <div className="flex min-w-0 flex-1 flex-col">
                                                        <span className="text-xs text-white font-bold">
                                                            {t('tracks.mileage_section')}
                                                        </span>
                                                        {!mileageOpen && (
                                                            <span className="text-[10px] text-gray-400">
                                                                {values.km != null && Number(values.km) > 0
                                                                    ? t('tracks.mileage_section_summary', {
                                                                        km: values.km,
                                                                    })
                                                                    : t('tracks.mileage_section_hint')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="shrink-0 rounded bg-gray-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                                        {mileageOpen
                                                            ? t('tracks.mileage_section_hide')
                                                            : t('tracks.mileage_section_show')}
                                                    </span>
                                                </button>
                                                {mileageOpen && (
                                                    <div className="border-t border-gray-600 px-2 py-2 flex flex-col gap-y-1">
                                                        <label
                                                            htmlFor="active-track-km"
                                                            className="text-xs text-white font-medium"
                                                        >
                                                            {t('tracks.km')}
                                                        </label>
                                                        <Field
                                                            id="active-track-km"
                                                            type="number"
                                                            name="km"
                                                            min="0"
                                                            step="0.1"
                                                            className="block w-28 text-center text-white bg-gray-900 border-0 placeholder:text-sm focus:ring-0 border-white border-b-2"
                                                            placeholder={t('tracks.km_placeholder') || ''}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {(dirty || (!hasClient && !!values.title) || (hasClient && !selectedProject)) && (
                                            <div className="flex flex-col justify-center items-center mt-2 gap-y-1">
                                                <Button
                                                    type="submit"
                                                    title={t('common.save')}
                                                    icon={(loadingActive === 'pending') ? 'loading' : 'check'}
                                                    color="none"
                                                    textColor="white"
                                                    iconColor="white"
                                                    className="inline ml-1"
                                                />
                                            {clientError && (
                                                <div className="text-center text-xs font-bold text-red-600">
                                                    {clientError}
                                                </div>
                                            )}
                                            </div>
                                        )}
                                    </div>
                                    )}
                                </div>
                            </div>
                        </Form>
                        );
                    }}
                </Formik>
            )}
            {editingTrack && (
                <TrackEditModal
                    show
                    track={editingTrack}
                    readOnly={!(
                        user?._id
                        && (typeof editingTrack.author === 'string'
                            ? editingTrack.author
                            : editingTrack.author?._id) === user._id
                    )}
                    onClose={() => {
                        setEditingId(undefined);
                        dispatch(loadSuggestedTracks({ limit: 10 }));
                    }}
                />
            )}
        </div>
    );
}

export default ActiveTrack;
